import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createClient, type SupabaseClientOptions } from '@supabase/supabase-js';

// Avoid sharing auth storage with the primary client so browsers do not
// instantiate multiple GoTrue clients under the same key (which triggers warnings).
const serviceClientConfig: SupabaseClientOptions<'public'> = {
  auth: {
    storageKey: 'supabase.service.auth',
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
};

// Service client for privileged operations (bypasses RLS)
const supabaseService = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY,
  serviceClientConfig
);
import { edgeAIClient } from '@/integrations/ai/edge-client';
import { AIChatModeConfig } from './useAIChatModeConfig';
import { updatePiiChatReferences, updatePiiChatReferencesNew, getPiiChatUserInformation, type QuestionAnswers } from '@/utils/piiUtils';
import { getPiiTokenForConversation, type UserInfoItem } from '@/utils/chatDataUtils';
import { FieldDictionary } from '@/types/fieldsDictionary';
import { VariableMapping } from '@/types/supabase';

interface ExtractedField {
  name: string;
  value: string;
  confidence: number;
}

type PingPostProviderRecord = {
  id: string;
  name: string;
  url: string;
  secret_key?: string | null;
  user_id: string;
  variable_mappings: VariableMapping[];
  is_enabled?: boolean | null;
  is_global?: boolean | null;
  chatbot_id?: string | null;
};

type ExtractedFieldMap = Record<string, string>;
type ChatbotOwner = { user_id: string | null };

// Simple in-memory cache per session for provider list by chatbot owner + allowed ids
const providerCache = new Map<string, PingPostProviderRecord[]>();

/**
 * Hook for AI data collection that integrates with existing PingPost system
 * Uses surgical approach to minimize changes to existing codebase
 */
export const useAIDataCollection = () => {

  /**
   * Extract data from AI conversation and integrate with trigger system
   * @param conversationId - The conversation ID
   * @param chatbotId - The chatbot ID
   * @param sessionId - The session ID
   * @param config - AI chat mode configuration
   * @param providedFieldsDictionary - Optional pre-fetched fields dictionary
   * @param providedUserInfo - Optional pre-fetched user info
   * @returns Updated userInfo after successful PII save, or null if no update occurred
   */
  const processAIDataCollection = useCallback(async (
    conversationId: string,
    chatbotId: string,
    sessionId: string,
    config: AIChatModeConfig,
    providedFieldsDictionary?: FieldDictionary[],
    providedUserInfo?: UserInfoItem[]
  ): Promise<UserInfoItem[] | null> => {
    // Use provided fieldsDictionary or fetch if not provided
    const fieldsDictionary = providedFieldsDictionary || [];

    // userInfo can be used for future enhancements (e.g., skip already collected fields)
    const userInfo = providedUserInfo || [];

    console.log('🔍 AI Data Collection - Starting process', {
      conversationId,
      chatbotId,
      sessionId
    });

    try {
      const messages = await fetchConversationMessages(conversationId);
      if (!messages || messages.length === 0) return null;

      const extractedData = await extractDataWithAI(messages, config, chatbotId, fieldsDictionary, userInfo);
      if (extractedData.length === 0) return null;

      let updatedUserInfo: UserInfoItem[]

      // Get fresh conversation metadata before save operation
      const conversationForSave = await getConversationMetadata(conversationId);
      await saveAsVirtualResponsesSafe(conversationId, chatbotId, extractedData, conversationForSave);

      // Resolve chatbot owner once and reuse
      const chatbotOwner = await fetchChatbotOwner(chatbotId);
      if (!chatbotOwner?.user_id) {
        console.error('❌ AI Data Collection - No chatbot owner found, aborting');
        return null;
      }

      const essentialFields = ['first_name', 'last_name', 'email', 'phone'];
      const userInfoKeys = new Set(userInfo.map(u => Object.keys(u)[0]));
      const isFieldInUserInfo = (fieldKey: string) => userInfoKeys.has(fieldKey);

      const requiredFromConfig = collectRequiredFieldKeys(config, fieldsDictionary, isFieldInUserInfo);
      const extractedFieldNames = extractedData.map(d => d.name);

      const hasAllConfigRequired = requiredFromConfig.every(field => extractedFieldNames.includes(field));
      const hasAllEssentialFields = essentialFields.every(field => extractedFieldNames.includes(field));

      console.log('🎯 AI Data Collection - Required/mapping checks', {
        requiredFromConfig,
        extractedFieldNames,
        hasAllConfigRequired,
        hasAllEssentialFields,
        userInfoKeys,
        userInfo
      });

      // Persist new fields to PII if we have any that weren't in userInfo
      const newFieldNames = extractedFieldNames.filter(name => !userInfoKeys.has(name));

      // Check if any extracted fields have different values than existing userInfo
      const existingUserInfoMap = new Map<string, string>();
      userInfo.forEach(infoItem => {
        Object.entries(infoItem).forEach(([key, value]) => {
          if (typeof value === 'string') {
            existingUserInfoMap.set(key, value);
          }
        });
      });

      const hasDifferentValues = extractedData.some(extractedField => {
        const existingValue = existingUserInfoMap.get(extractedField.name);
        return existingValue !== undefined && existingValue !== extractedField.value;
      });

      if (hasAllEssentialFields && (newFieldNames.length > 0 || hasDifferentValues)) {
        console.log('💾 Saving new fields to PII', { newFieldNames, hasDifferentValues });
        updatedUserInfo = await saveExtractedDataToPii(conversationId, chatbotId, sessionId, extractedData);
      }

      // Mark conversation completed only when all required fields are present
      if (hasAllConfigRequired) {
        // Get fresh conversation metadata before completion operation
        const conversationForCompletion = await getConversationMetadata(conversationId);
        await markConversationAsCompleted(conversationId, conversationForCompletion);
      }

      // Send to pingpost providers when mapping requirements are satisfied
      const providerIds = collectProviderIdsFromConfig(config);
      const dictionaryKeyById = Object.fromEntries(
        (fieldsDictionary || []).map(fd => [fd.id, fd.key])
      );
      // Get fresh conversation metadata before pingpost operation
      const conversationForPingPost = await getConversationMetadata(conversationId);
      await sendPiiDataToPingPostProviders(
        conversationId,
        chatbotId,
        sessionId,
        extractedData,
        providerIds,
        dictionaryKeyById,
        chatbotOwner.user_id,
        conversationForPingPost
      );

      return updatedUserInfo || userInfo;
    } catch (error) {
      console.error('❌ Error processing AI data collection:', error);
      return null;
    }
  }, []);

  return { processAIDataCollection };
};

async function fetchConversationMessages(conversationId: string) {
  const { data: messages } = await supabase
    .from('messages')
    .select('content, type, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  console.log('📝 AI Data Collection - Messages retrieved', {
    messageCount: messages?.length || 0,
    messages: messages?.map(m => ({ type: m.type, content: m.content.substring(0, 100) }))
  });

  return messages;
}

const collectRequiredFieldKeys = (
  config: AIChatModeConfig,
  fieldsDictionary: FieldDictionary[],
  isFieldInUserInfo: (field: string) => boolean
): string[] => {
  const keys = new Set<string>();

  config.offerGoals?.goals?.forEach(goal => {
    goal.actions?.forEach(action => {
      if (action.type === 'fields_set' && Array.isArray(action.requiredFields)) {
        action.requiredFields.forEach(f => {
          const key = fieldsDictionary.find(item => item.id === f.id)?.key;
          if (key) keys.add(key);
        });
      }
    });
  });

  return Array.from(keys).filter(key => !isFieldInUserInfo(key));
};

const collectProviderIdsFromConfig = (config: AIChatModeConfig): string[] => {
  const ids = new Set<string>();
  config.offerGoals?.goals?.forEach(goal => {
    goal.actions?.forEach(action => {
      action.triggersPingPost?.forEach(p => {
        if (p.id) ids.add(p.id);
      });
    });
  });
  return Array.from(ids);
};

const saveAsVirtualResponsesSafe = async (
  conversationId: string,
  chatbotId: string,
  extractedData: ExtractedField[],
  conversation: any
) => {
  try {
    console.log('🔄 [NEW CODE] About to call saveAsVirtualResponses', { extractedDataCount: extractedData.length, timestamp: new Date().toISOString() });
    await saveAsVirtualResponses(conversationId, chatbotId, extractedData, conversation);
    console.log('💾 AI Data Collection - Data saved as virtual responses');
  } catch (saveError) {
    console.error('❌ Error in saveAsVirtualResponses:', saveError);
  }
};

const fetchChatbotOwner = async (chatbotId: string): Promise<ChatbotOwner | null> => {
  const { data, error } = await supabase
    .from('chatbots')
    .select('user_id')
    .eq('id', chatbotId)
    .single();

  if (error) {
    console.error('❌ Error fetching chatbot owner', error);
    return null;
  }

  return data as ChatbotOwner;
};

const fetchProvidersWithCache = async (
  ownerUserId: string,
  allowedProviderIds: string[]
): Promise<PingPostProviderRecord[]> => {
  const cacheKey = `${ownerUserId}:${allowedProviderIds.sort().join(',')}`;
  if (providerCache.has(cacheKey)) {
    return providerCache.get(cacheKey) || [];
  }

  const { data: providers, error: providersError } = await supabase
    .from('pingpost_providers')
    .select('id, name, url, secret_key, variable_mappings, is_enabled, is_global, user_id, chatbot_id')
    .eq('user_id', ownerUserId)
    .in('id', allowedProviderIds)
    .eq('is_enabled', true);

  if (providersError) throw providersError;

  const normalizedProviders: PingPostProviderRecord[] = (providers || []).map(provider => ({
    ...provider,
    variable_mappings: Array.isArray(provider.variable_mappings) ? provider.variable_mappings : [],
    secret_key: provider.secret_key ?? null,
  }));

  providerCache.set(cacheKey, normalizedProviders);
  return normalizedProviders;
};

const getSentProvidersForConversation = async (conversationId: string, conversation: any): Promise<Set<string>> => {
  if (!conversation) {
    return new Set();
  }

  const metadata = conversation?.metadata as any || {};
  const sentProviders = metadata.sent_pingpost_providers || [];
  return new Set(sentProviders);
};

const markProvidersAsSent = async (conversationId: string, providerIds: string[], conversation: any): Promise<void> => {
  if (!conversation) {
    return ;
  }

  const currentMetadata = (conversation?.metadata as any) || {};
  const sentProviders = currentMetadata.sent_pingpost_providers || [];

  // Add new provider IDs that aren't already marked as sent
  const newProviders = providerIds.filter(id => !sentProviders.includes(id));

  if (newProviders.length > 0) {
    const updatedMetadata = {
      ...currentMetadata,
      sent_pingpost_providers: [...sentProviders, ...newProviders]
    };

    const { error: updateError } = await supabase
      .from('conversations')
      .update({ metadata: updatedMetadata })
      .eq('id', conversationId);

    if (updateError) {
      console.error('❌ Error updating conversation metadata with sent providers:', updateError);
    } else {
      console.log('✅ Marked providers as sent in conversation metadata', {
        conversationId,
        providerIds: newProviders
      });
    }
  }
};

/**
 * Extract structured data using AI (minimal implementation)
 */
async function extractDataWithAI(
  messages: any[],
  config: AIChatModeConfig,
  chatbotId: string,
  fieldsDictionary: any[],
  userInfo: UserInfoItem[]
): Promise<ExtractedField[]> {

  const allFields: string[] = [];
  const allFieldsDescription: string[] = [];
  
  // Add ALL fields from fieldsDictionary (not just requiredFields)
  // This allows AI to extract any relevant data from conversation
  fieldsDictionary.forEach(dictItem => {
    if (dictItem.key && !allFields.includes(dictItem.key)) {
      allFields.push(dictItem.key);
      allFieldsDescription.push(`${dictItem.key} - (${dictItem.description || 'No description'})`);
    }
  });

  console.log('🤖 AI Extraction - Starting', {
    allFields,
    fieldsDictionaryCount: fieldsDictionary.length,
    messageCount: messages.length,
    userInfoCount: userInfo.length
  });

  if (allFields.length === 0) {
    console.log('❌ AI Extraction - No fields to extract');
    return [];
  }

  const userInfoFields: ExtractedField[] = [];
  if (userInfo && userInfo.length > 0) {
    console.log('🤖 AI Extraction - Processing userInfo', { userInfo });

    userInfo.forEach(infoItem => {
      Object.entries(infoItem).forEach(([key, value]) => {
        if (allFields.includes(key) && value && typeof value === 'string' && value.trim()) {
          userInfoFields.push({
            name: key,
            value: value.trim(),
            confidence: 0.7
          });

          console.log('🤖 AI Extraction - Added userInfo field', {
            field: key,
            value: value.trim(),
            confidence: 0.7
          });
        }
      });
    });
  }

  // MESSAGE FILTERING BEFORE SENDING TO AI
  // Remove system messages about button presses
  const filteredMessages = messages.filter(msg => {
    const content = msg.content || '';
    
    // // Ignore system messages about button presses
    // if (msg.type !== 'user') {
    //   return false; // Ignore all non-user messages
    // }
    
    // Ignore user messages that look like system events
    const isSystemEvent = 
      content.startsWith('The user pressed the');
    
    return !isSystemEvent;
  });

  console.log('🤖 AI Extraction - Messages filtered', {
    originalCount: messages.length,
    filteredCount: filteredMessages.length,
    removedMessages: messages.length - filteredMessages.length
  });

  // If no messages after filtering, return only userInfo
  if (filteredMessages.length === 0) {
    console.log('🤖 AI Extraction - No user messages after filtering');
    return userInfoFields;
  }

  // Build conversation context only from filtered messages
  const conversationText = filteredMessages
    .map(msg => `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
    .join('\n');

  // Create extraction prompt with more explicit instructions
  const prompt = `Extract information for these fields from the conversation.

CRITICAL INSTRUCTION: IGNORE ALL SYSTEM-GENERATED MESSAGES ABOUT BUTTON PRESSES. 
These messages (like "The user pressed the ...") are NOT user-provided information. 
They are automated system events that should NEVER be treated as user's personal data.

Available fields to extract:
${allFieldsDescription.map((desc, i) => `${i + 1}. ${desc}`).join('\n')}

Conversation (already filtered to remove system events):
${conversationText}

Extraction Rules:
1. Extract ONLY from actual user messages where the user is providing information about themselves
2. COMPLETELY IGNORE any system-generated content, button press logs, or automated messages
3. Only include fields where you found actual data in genuine user messages
4. Use confidence scores:
  - 0.95+ for clear, explicit user statements ("My name is John")
  - 0.8-0.94 for inferred but clear information
  - 0.7-0.79 for contextual clues
  - BELOW 0.7: reject - too uncertain
5. If user corrects information, use the MOST RECENT valid value
6. NEVER extract from system notifications about button presses

Return JSON array: [{"name": "field_name", "value": "extracted_value", "confidence": 0.95}]
Only include fields with confidence >= 0.7.`;

  console.log('🤖 AI Extraction - Prompt created', {
    prompt: prompt,
    promptLength: prompt.length,
    conversationLength: conversationText.length
  });

  try {
    console.log('🤖 AI Extraction - Calling chat-ai edge function directly');
    
    // Get Supabase credentials
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Supabase URL or key is missing');
    }
    
    // Call edge function directly
    const url = `${supabaseUrl}/functions/v1/chat-ai`;
    const edgeResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
        'apikey': supabaseKey
      },
      body: JSON.stringify({
        message: 'Extract the data following all rules, especially ignoring system button events.',
        previousMessages: [
          { text: prompt, sender: 'system' }
        ],
        chatbotId
      })
    });
    
    if (!edgeResponse.ok) {
      const errorText = await edgeResponse.text();
      console.error(`Edge Function error: ${edgeResponse.status} ${errorText}`);
      throw new Error(`Edge Function error: ${edgeResponse.status} ${errorText}`);
    }
    
    const aiResponse = await edgeResponse.json();
    console.log('🤖 AI Extraction - EdgeAI response received', { aiResponse });
    const response = aiResponse.message;

    console.log('🤖 AI Extraction - Raw response', { response });

    // Handle markdown-wrapped JSON responses
    let jsonString = response;
    if (response.includes('```json')) {
      const jsonMatch = response.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
    } else if (response.includes('```')) {
      const codeMatch = response.match(/```\s*([\s\S]*?)\s*```/);
      if (codeMatch) {
        jsonString = codeMatch[1].trim();
      }
    }
    
    console.log('🤖 AI Extraction - Cleaned JSON string', { jsonString });
    const extracted = JSON.parse(jsonString) as ExtractedField[];
    const filtered = extracted.filter(d => d.confidence >= 0.7 && allFields.includes(d.name));

    console.log('🤖 AI Extraction - Parsed and filtered', {
      extracted,
      filtered,
      confidenceThreshold: 0.7
    });

    const mergedFields = new Map<string, ExtractedField>();

    userInfoFields.forEach(field => {
      mergedFields.set(field.name, field);
    });

    filtered.forEach(aiField => {
      mergedFields.set(aiField.name, aiField);
      // const existingField = mergedFields.get(aiField.name);
      //
      // if (!existingField) {
      //   mergedFields.set(aiField.name, aiField);
      // } else if (aiField.confidence > existingField.confidence) {
      //   mergedFields.set(aiField.name, aiField);
      // } else if (aiField.confidence === existingField.confidence && aiField.value !== existingField.value) {
      //   mergedFields.set(aiField.name, aiField);
      // }
    });

    const result = Array.from(mergedFields.values());

    console.log('🤖 AI Extraction - Final merged result', {
      userInfoFieldsCount: userInfoFields.length,
      aiExtractedFieldsCount: filtered.length,
      finalResultCount: result.length,
      fields: result.map(f => ({ name: f.name, confidence: f.confidence }))
    });

    return result;
  } catch (error) {
    console.error('❌ AI extraction error:', error);
    return userInfoFields || []; // Return at least userInfo on error
  }
}

/**
 * Save extracted data as virtual user responses (hooks into existing system)
 */
async function saveAsVirtualResponses(
  conversationId: string,
  chatbotId: string,
  extractedData: ExtractedField[],
  conversation: any
): Promise<void> {
  console.log('💾 Saving virtual responses', {
    conversationId,
    chatbotId,
    extractedDataCount: extractedData.length
  });

  // TEMPORARY FIX: Store extracted data in conversation metadata until RLS is fixed
  console.log('💾 Storing AI-extracted data in conversation metadata (RLS workaround)');

  try {
    if (!conversation) {
      return ;
    }

    // Create a summary object of extracted data
    const extractedDataSummary = {};
    for (const data of extractedData) {
      extractedDataSummary[data.name] = data.value;
      console.log('💾 Processing field', { fieldName: data.name, value: data.value });
    }

    // Merge with existing metadata, setting completed = true
    const currentMetadata = (conversation?.metadata as any) || {};
    const updatedMetadata = {
      ...currentMetadata,
      ai_extracted_data: extractedDataSummary,
      extracted_at: new Date().toISOString(),
      extraction_method: 'ai_chat_mode'
    };

    // Store in conversation metadata using regular supabase client
    const { data: conversationUpdate, error: updateError } = await supabase
      .from('conversations')
      .update({ metadata: updatedMetadata })
      .eq('id', conversationId);

    if (updateError) {
      console.error('❌ Error storing AI data in conversation metadata:', updateError);
    } else {
      console.log('✅ AI-extracted data stored in conversation metadata', {
        conversationId,
        extractedFields: Object.keys(extractedDataSummary),
        data: extractedDataSummary
      });
    }

  } catch (metadataError) {
    console.error('❌ Error with metadata storage approach:', metadataError);
    
    // Fallback: Try to find existing questions and save responses
    console.log('💾 Fallback: Attempting to use existing questions');
    
    for (const data of extractedData) {
      console.log('💾 Processing field (fallback)', { fieldName: data.name, value: data.value });
      
      // Try to find existing questions by text pattern
      const { data: existingQuestions, error: searchError } = await supabase
        .from('questions')
        .select('id, text')
        .eq('chatbot_id', chatbotId)
        .ilike('text', `%${data.name}%`);
      
      if (existingQuestions && existingQuestions.length > 0) {
        const questionId = existingQuestions[0].id;
        console.log('💾 Found existing question', { fieldName: data.name, questionId });
        
        const { data: insertResult, error: insertError } = await supabase
          .from('user_responses')
          .insert({
            chatbot_id: chatbotId,
            conversation_id: conversationId,
            question_id: questionId,
            response_text: data.value
          } as any);
          
        if (insertError) {
          console.error('❌ Error saving to existing question:', insertError);
        } else {
          console.log('✅ Saved to existing question', { fieldName: data.name });
        }
      } else {
        console.log('⚠️ No existing question found for:', data.name);
      }
    }
  }
}

/**
 * Send PII data to PingPost Providers configured for this chatbot
 */
async function sendPiiDataToPingPostProviders(
  conversationId: string,
  chatbotId: string,
  sessionId: string,
  extractedData: ExtractedField[],
  allowedProviderIds: string[],
  dictionaryKeyById: Record<string, string>,
  ownerUserId: string,
  conversation: any
): Promise<void> {
  try {
    console.log('📤 PingPost - Starting to send PII data to providers', {
      conversationId,
      chatbotId,
      sessionId,
      extractedDataCount: extractedData.length,
      allowedProviderIds
    });

    if (!allowedProviderIds.length) {
      console.log('ℹ️ PingPost - No providers configured for this chatbot, skipping');
      return;
    }

    // Fetch provider details from database (global for the chatbot's owner) filtered by configured provider ids, with cache
    const normalizedProviders = await fetchProvidersWithCache(ownerUserId, allowedProviderIds);

    if (!normalizedProviders.length) {
      console.log('⚠️ PingPost - No active providers found');
      return;
    }

    // Check which providers have already been sent to for this conversation
    const sentProviders = await getSentProvidersForConversation(conversationId, conversation);

    const extractedFieldNames = new Set(extractedData.map(d => d.name));

    // Filter providers: only those not sent yet and have all required mapping fields
    const providersToSend = normalizedProviders.filter(provider => {
      // Skip if already sent
      if (sentProviders.has(provider.id)) {
        console.log('⏭️ PingPost - Skipping already sent provider', {
          providerId: provider.id,
          providerName: provider.name
        });
        return false;
      }

      // Check if all mapping keys for this provider are present
      const providerMappingKeys = (provider.variable_mappings || [])
        .map(m => dictionaryKeyById[m.key_dictionary_id])
        .filter(Boolean);

      const hasAllRequiredForProvider = providerMappingKeys.every(key => extractedFieldNames.has(key));

      if (!hasAllRequiredForProvider) {
        console.log('⚠️ PingPost - Skipping provider, missing mapping fields', {
          providerId: provider.id,
          providerName: provider.name,
          requiredKeys: providerMappingKeys,
          availableKeys: Array.from(extractedFieldNames)
        });
      }

      return hasAllRequiredForProvider;
    });

    if (!providersToSend.length) {
      console.log('ℹ️ PingPost - No providers need sending (already sent or missing fields)');
      return;
    }

    // Get PII token to include in payload
    // const piiToken = await getPiiTokenForConversation(conversationId, chatbotId, sessionId);

    // Send to providers using mapped variable names
    console.log('📤 PingPost - Preparing mapped payloads for providers', {
      providerCount: providersToSend.length,
      providers: providersToSend.map(p => ({ id: p.id, name: p.name, url: p.url }))
    });

    // Collect successful sends to update metadata once at the end
    const successfulProviders: string[] = [];

    const results = await Promise.allSettled(
      providersToSend.map(async (provider) => {
        try {
          // Build mapped payload based on variable_mappings
          const mappedPiiData: Record<string, string> = {};

          (provider.variable_mappings || []).forEach(mapping => {
            const fieldKey = dictionaryKeyById[mapping.key_dictionary_id];
            if (!fieldKey) return;
            const matched = extractedData.find(d => d.name === fieldKey);
            if (!matched || !matched.value) return;
            mappedPiiData[mapping.post_name] = matched.value;
          });

          if (!Object.keys(mappedPiiData).length) {
            console.log('⚠️ PingPost - Skipping provider, no mapped data', {
              providerId: provider.id,
              providerName: provider.name
            });
            return { success: false, providerId: provider.id, providerName: provider.name, reason: 'no_mapped_data' };
          }

          // Prepare formData (mapped keys)
          const formData = new FormData();

          Object.keys(mappedPiiData).forEach(key => {
            formData.append(key, mappedPiiData[key]);
          });

          // const payload = {
          //   chatbot_id: chatbotId,
          //   conversation_id: conversationId,
          //   session_id: sessionId,
          //   timestamp: new Date().toISOString(),
          //   pii_data: mappedPiiData,
          //   extracted_fields: extractedData.map(d => ({
          //     name: d.name,
          //     value: d.value,
          //     confidence: d.confidence
          //   })),
          //   ...(piiToken && { pii_token: piiToken })
          // };

          const headers: Record<string, string> = {
            // 'Content-Type': 'application/json',
            'User-Agent': 'OfferChat-PingPost/1.0'
          };

          if (provider.secret_key) {
            headers['Authorization'] = `Bearer ${provider.secret_key}`;
            headers['X-API-Key'] = provider.secret_key;
          }

          console.log('📤 PingPost - Sending to provider', {
            providerId: provider.id,
            providerName: provider.name,
            url: provider.url,
            mappedKeys: Object.keys(mappedPiiData)
          });

          const response = await fetch(provider.url, {
            method: 'POST',
            headers,
            body: formData
          });

          const responseText = await response.text();
          let responseData;
          try {
            responseData = responseText ? JSON.parse(responseText) : null;
          } catch {
            responseData = { raw: responseText };
          }

          if (response.ok) {
            console.log('✅ PingPost - Successfully sent to provider', {
              providerId: provider.id,
              providerName: provider.name,
              status: response.status
            });

            // Collect successful provider for metadata update
            successfulProviders.push(provider.id);

            return { success: true, providerId: provider.id, providerName: provider.name, status: response.status };
          } else {
            console.error('❌ PingPost - Provider returned error', {
              providerId: provider.id,
              providerName: provider.name,
              status: response.status,
              response: responseData
            });
            return { success: false, providerId: provider.id, providerName: provider.name, status: response.status, error: responseData };
          }
        } catch (error) {
          console.error('❌ PingPost - Error sending to provider', {
            providerId: provider.id,
            providerName: provider.name,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          return { success: false, providerId: provider.id, providerName: provider.name, error: error instanceof Error ? error.message : 'Unknown error' };
        }
      })
    );

    // Update conversation metadata once with all successful providers
    if (successfulProviders.length > 0) {
      await markProvidersAsSent(conversationId, successfulProviders, conversation);
    }

    // Log summary
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failed = results.length - successful;

    console.log('📤 PingPost - Summary', {
      total: providersToSend.length,
      successful,
      failed,
      results: results.map((r, i) => ({
        provider: providersToSend[i]?.name,
        status: r.status === 'fulfilled' ? (r.value.success ? 'success' : 'failed') : 'rejected',
        details: r.status === 'fulfilled' ? r.value : r.reason
      }))
    });

  } catch (error) {
    console.error('❌ PingPost - Error sending PII data to providers:', error);
  }
}

/**
 * get conversation metadata
 */
async function getConversationMetadata(conversationId: string) {
  try {
    // Get current conversation metadata
    const { data: conversation, error: fetchError } = await supabase
      .from('conversations')
      .select('metadata')
      .eq('id', conversationId)
      .single();

    if (fetchError) {
      console.error('❌ Error fetching conversation metadata:', fetchError);
      return null;
    }

    return conversation;
  } catch (error) {
    console.error('❌ Error marking conversation as completed:', error);
    return null;
  }
}


/**
 * Mark conversation as completed in metadata
 */
async function markConversationAsCompleted(conversationId: string, conversation: any): Promise<void> {
  try {
    console.log('✅ Marking conversation as completed', { conversationId });

    if (!conversation) {
      return;
    }

    // Merge with existing metadata, setting completed = true
    const currentMetadata = (conversation?.metadata as any) || {};
    const updatedMetadata = {
      ...currentMetadata,
      completed: true,
      completed_at: new Date().toISOString()
    };

    // Update conversation metadata
    const { error: updateError } = await supabase
      .from('conversations')
      .update({ metadata: updatedMetadata })
      .eq('id', conversationId);

    if (updateError) {
      console.error('❌ Error updating conversation metadata:', updateError);
    } else {
      console.log('✅ Conversation marked as completed in metadata', {
        conversationId,
        completed: true
      });
    }
  } catch (error) {
    console.error('❌ Error marking conversation as completed:', error);
  }
}

/**
 * Save extracted data to PII data associated with this chat and conversation
 * Returns updated userInfo after successful save
 */
async function saveExtractedDataToPii(
  conversationId: string,
  chatbotId: string,
  sessionId: string,
  extractedData: ExtractedField[]
): Promise<UserInfoItem[] | null> {
  try {
    console.log('🔒 PII Save - Starting to save extracted data to PII', {
      conversationId,
      chatbotId,
      sessionId,
      extractedDataCount: extractedData.length
    });

    // Get PII token
    const piiToken = await getPiiTokenForConversation(conversationId, chatbotId, sessionId);
    
    if (!piiToken) {
      console.log('⚠️ PII Save - No PII token found, skipping PII save');
      return null;
    }

    // Convert extracted data to QuestionAnswers format
    // Format: { fieldName: value }
    const questionAnswers: QuestionAnswers = {};
    extractedData.forEach(data => {
      questionAnswers[data.name] = data.value;
    });

    console.log('🔒 PII Save - Saving to PII', {
      tokenPreview: piiToken.slice(0, 8) + '...',
      chatbotId,
      fieldsCount: Object.keys(questionAnswers).length,
      fields: Object.keys(questionAnswers)
    });

    // Update PII chat references
    const result = await updatePiiChatReferencesNew(conversationId, questionAnswers, piiToken);
    // const result = await updatePiiChatReferences(piiToken, chatbotId, questionAnswers);

    if (result.success) {
      console.log('✅ PII Save - Successfully saved extracted data to PII', {
        chatbotId,
        fieldsCount: Object.keys(questionAnswers).length
      });

      // Reload userInfo from PII after successful save
      try {
        const { success, data: user } = await getPiiChatUserInformation(conversationId, piiToken);
        if (success && user?.fields) {
          const updatedUserInfo: UserInfoItem[] = [];
          Object.entries(user.fields).forEach(([key, value]) => {
            updatedUserInfo.push({ [key]: (value as any)?.value || '' });
          });
          
          console.log('✅ PII Save - UserInfo reloaded after save', {
            fieldsCount: updatedUserInfo.length,
            fields: updatedUserInfo.map(u => Object.keys(u)[0])
          });
          
          return updatedUserInfo;
        }
      } catch (reloadError) {
        console.error('❌ PII Save - Error reloading userInfo:', reloadError);
      }
      
      return null;
    } else {
      console.error('❌ PII Save - Failed to save to PII', {
        error: result.error
      });
      return null;
    }
  } catch (error) {
    console.error('❌ PII Save - Error saving extracted data to PII:', error);
    return null;
  }
}

/**
 * Trigger webhooks using existing trigger system
 */
async function triggerWebhooks(
  chatbotId: string,
  sessionId: string,
  conversationId: string,
  extractedData: ExtractedField[]
): Promise<void> {
  try {
    console.log('🎯 Trigger Webhooks - Fetching trigger rules', { chatbotId });
    
    // Get trigger rules
    const { data: triggerRules, error: rulesError } = await supabase
      .from('trigger_rules')
      .select('*')
      .eq('chatbot_id', chatbotId);

    if (rulesError) {
      console.error('❌ Error fetching trigger rules:', rulesError);
      return;
    }

    console.log('🎯 Trigger Webhooks - Rules fetched', {
      rulesCount: triggerRules?.length || 0,
      rules: triggerRules?.map(rule => ({ id: rule.id, name: rule.name, active: rule.active }))
    });

    if (!triggerRules || triggerRules.length === 0) {
      console.log('⚠️ Trigger Webhooks - No trigger rules found for chatbot');
      return;
    }

    // Build user responses for trigger evaluation
    const userResponses: Record<string, string> = {};
    extractedData.forEach(data => {
      // Use the format expected by trigger executor for data collection fields
      userResponses[`ai_extracted_${data.name}`] = data.value;
      // Also add the field name directly for backward compatibility
      userResponses[data.name] = data.value;
    });

    console.log('🎯 Trigger Webhooks - User responses prepared', {
      responseCount: Object.keys(userResponses).length,
      responses: userResponses
    });

    // Use existing trigger executor
    console.log('🎯 Trigger Webhooks - Calling trigger executor...');
    const { triggerExecutor } = await import('@/utils/triggerExecutor');
    await triggerExecutor.evaluateAndExecuteTriggers(triggerRules, {
      chatbotId,
      sessionId,
      conversationId,
      userResponses
    });
    
    console.log('✅ Trigger Webhooks - Trigger execution completed');

  } catch (error) {
    console.error('Error triggering webhooks:', error);
  }
}
